import { useMutation, useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { CATERING_SECTIONS, CATERING_SECTION_LABELS, type TraySize, type FulfillmentType, type CateringSectionKey } from '@/types/app'

export interface TrayOption {
  available: boolean
  serves: number | null
  price: number | null
  // Per-lb sizes only — minimum order (lbs). Absent/null for trays/pieces.
  min?: number | null
}

// The tray sizes a customer can actually pick — 'custom' is a staff-only
// escape hatch and never appears in the public matrix.
export type TrayKey = Exclude<TraySize, 'custom'>

// A single item+size line the customer has picked. Keyed (not just grouped)
// by itemId+traySize so the same dish can be selected as e.g. one Large tray
// AND one Small tray at once — two distinct entries, not one overwriting the other.
export interface SelectionEntry {
  itemId: string
  traySize: TrayKey
  quantity: number
  notes: string | null
  // Staff-set only — the customer never marks their own items complimentary,
  // just inherits whatever staff already set when the order was last saved.
  isComplimentary: boolean
  // Meal-occasion (Breakfast/Lunch/Evening/Dinner) — a fixed, standard set
  // (see CATERING_SECTIONS), assigned per order line item. New items the
  // customer adds via Browse have none until someone assigns one;
  // already-saved items keep whatever they had.
  sectionId: CateringSectionKey | null
}

export function selectionKey(itemId: string, traySize: TrayKey) {
  return `${itemId}:${traySize}`
}

// Groups any list of category-tagged rows (menu items, order line items, …)
// into category sections ordered the same way as the rest of the app —
// by display_order, with anything uncategorized (deleted/renamed product)
// trailing under "Other" rather than disappearing.
export function groupByCategory<T extends { categoryId: string | null }>(
  rows: T[],
  categories: PublicMenuCategory[],
): { id: string; name: string; rows: T[] }[] {
  const byCat = new Map<string, T[]>()
  for (const row of rows) {
    const key = row.categoryId ?? 'uncategorized'
    byCat.set(key, [...(byCat.get(key) ?? []), row])
  }
  const sorted = [...categories].sort((a, b) => a.display_order - b.display_order)
  const sections = sorted
    .filter(c => byCat.has(c.id))
    .map(c => ({ id: c.id, name: c.name, rows: byCat.get(c.id)! }))
  if (byCat.has('uncategorized')) {
    sections.push({ id: 'uncategorized', name: 'Other', rows: byCat.get('uncategorized')! })
  }
  return sections
}

export interface PublicMenuItem {
  id: string
  category_id: string | null
  name: string
  regional_name: string | null
  description: string | null
  dietary_flags: string[]
  allergens: string[]
  images: string[]
  is_addon: boolean
  tray_options: {
    half_tray: TrayOption
    medium_tray: TrayOption
    full_tray: TrayOption
    per_piece: TrayOption
    per_lb: TrayOption
    boxed_per_lb: TrayOption
  }
}

export interface PublicMenuCategory {
  id: string
  name: string
  display_order: number
}

// Builds the same {id, name, display_order}[] shape groupByCategory expects,
// from the fixed standard occasions selected for this event (canonical
// order) — sections apply to order LINE ITEMS, not catalog products, so
// there's nothing to fetch: the same dish can be on one order twice, once
// per occasion, which could never live on a PublicMenuItem.
export function eventSectionBuckets(eventSections: CateringSectionKey[]) {
  return CATERING_SECTIONS
    .filter(k => eventSections.includes(k))
    .map((k, i) => ({ id: k, name: CATERING_SECTION_LABELS[k], display_order: i }))
}

export interface PublicSelectedItem {
  menu_item_id: string | null
  name: string
  tray_size: TraySize | null
  quantity: number
  notes: string | null
  unit_price: number | null
  is_complimentary?: boolean
  catering_section?: CateringSectionKey | null
}

export interface PublicRestaurantBranding {
  name: string
  logo_url: string | null
  brand_colour_primary: string | null
}

export interface PublicContact {
  name: string
  phone: string | null
}

// Shared order-header shape — powers the top info card regardless of whether
// the menu itself is still editable.
export interface PublicOrderHeader {
  order_number: string
  client_name: string
  client_phone: string
  event_date: string
  event_time: string | null
  event_type: string | null
  guest_count: number | null
  special_requests: string | null
  status: string
  event_sections: CateringSectionKey[]
  fulfillment_type: FulfillmentType | null
  quote_amount: number | null
  delivery_fee: number | null
  delivery_is_complimentary: boolean
  discount_amount: number | null
  discount_percentage: number | null
}

export type UnlockResult =
  | {
      editable: true
      showPricing: boolean
      restaurant: PublicRestaurantBranding | null
      contact: PublicContact | null
      order: PublicOrderHeader
      categories: PublicMenuCategory[]
      items: PublicMenuItem[]
      selected: PublicSelectedItem[]
    }
  | {
      editable: false
      showPricing: boolean
      restaurant: PublicRestaurantBranding | null
      contact: PublicContact | null
      order: PublicOrderHeader
      categories: PublicMenuCategory[]
      items: PublicSelectedItem[]
    }

export interface SubmitItem {
  menu_item_id: string
  tray_size: TraySize
  quantity: number
  notes?: string | null
  // Carried through so a staff-assigned section survives the customer
  // editing quantities and resubmitting — the customer never sets this
  // themselves, just preserves whatever was already there.
  catering_section?: CateringSectionKey | null
}

async function callQuoteFunction<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('public-catering-quote', { body })
  if (error) {
    type FnError = { context?: Response }
    const context = (error as FnError).context
    const code = context ? ((await context.clone().json().catch(() => null)) as { error?: string } | null)?.error : null
    throw new Error(code ?? error.message)
  }
  return data as T
}

// Passcode-free — just the restaurant's public name/logo, so the phone-number
// entry screen itself is branded instead of looking like a generic form.
export function useCateringQuoteBranding(orderNumber: string | undefined) {
  return useQuery({
    queryKey: ['public-catering-quote', 'branding', orderNumber],
    queryFn: () => callQuoteFunction<{ restaurant: PublicRestaurantBranding | null }>({
      action: 'branding', order_number: orderNumber,
    }),
    enabled: !!orderNumber,
    staleTime: 5 * 60_000,
  })
}

export function useUnlockCateringQuote() {
  return useMutation({
    mutationFn: ({ orderNumber, passcode }: { orderNumber: string; passcode: string }) =>
      callQuoteFunction<UnlockResult>({ action: 'unlock', order_number: orderNumber, passcode }),
  })
}

export function useSubmitCateringQuote() {
  return useMutation({
    mutationFn: ({ orderNumber, passcode, items, notes, fulfillmentType }: {
      orderNumber: string
      passcode: string
      items: SubmitItem[]
      notes?: string
      fulfillmentType?: FulfillmentType | null
    }) =>
      callQuoteFunction<{ success: true }>({
        action: 'submit', order_number: orderNumber, passcode, items, notes,
        fulfillment_type: fulfillmentType ?? null,
      }),
  })
}
